import { TrendingDown, BarChart3, ListChecks, Zap, Wallet, ArrowRight } from 'lucide-react'

interface WelcomeModalProps {
  onDismiss: () => void
  onOpenGuide: () => void
}

/**
 * First-visit welcome modal. Briefly explains what DebtOS is and gives a
 * one-click path to either start exploring or read the full guide.
 *
 * Triggered by HelpProvider when localStorage doesn't yet have a "welcomed"
 * flag. Dismissing it sets the flag so it never shows again.
 */
export function WelcomeModal({ onDismiss, onOpenGuide }: WelcomeModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        {/* Hero */}
        <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <TrendingDown className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Welcome to DebtOS</h2>
              <p className="text-sm text-slate-400 mt-0.5">
                A personal debt optimization dashboard that runs entirely on your device.
              </p>
            </div>
          </div>
        </div>

        {/* Module overview */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-300">
            DebtOS gives you four tools to take control of your debt:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ModuleCard
              icon={<BarChart3 className="w-4 h-4 text-emerald-400" />}
              title="Dashboard"
              description="High-level snapshot — total debt, interest burn, and strategy preview"
            />
            <ModuleCard
              icon={<ListChecks className="w-4 h-4 text-blue-400" />}
              title="Debt Registry"
              description="Source of truth for every debt with sorting, filtering, and promo lifecycle"
            />
            <ModuleCard
              icon={<Zap className="w-4 h-4 text-amber-400" />}
              title="Strategy Engine"
              description="Compare avalanche, snowball, hybrid, and minimum-only payoff strategies"
            />
            <ModuleCard
              icon={<Wallet className="w-4 h-4 text-purple-400" />}
              title="Cash Flow"
              description="Monthly balance-the-books workflow with income, allocations, and survival floor"
            />
          </div>

          <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-3 mt-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="text-slate-300 font-semibold">Privacy:</span> All data stays in your
              browser's local storage. Nothing is sent to any server. Clearing browser data will
              erase everything — back up regularly when that feature ships.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-800 bg-slate-950/30">
          <button
            onClick={onOpenGuide}
            className="flex-1 px-4 py-2.5 border border-slate-700 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            Read the User Guide
          </button>
          <button
            onClick={onDismiss}
            className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            Start Exploring
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

function ModuleCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="bg-slate-800/40 border border-slate-700/60 rounded-lg p-3">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold text-white">{title}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{description}</p>
    </div>
  )
}
